import { z } from "zod";

/* ===========================================================================
 * US-E15.F5-0180-S02 — trạng thái thứ ba + ngưỡng xác nhận theo nguồn (@UC9223).
 *
 * Trạng thái trung thực thứ ba `transferred_unreconciled` sống trong PEER_SETTLEMENT_STATUS
 * (debts.ts — mở rộng additive). File này giữ MODE (nhánh nguồn/ngưỡng) + config ngưỡng cấp nhóm.
 *
 * B1: tiền = STRING minor unit (BigInt-as-string, KHÔNG float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * Mode xác nhận (mirror domain PEER_CONFIRM_MODE) — client hiển thị nhánh nguồn/ngưỡng ở màn nợ.
 * Nguồn duy nhất về giá trị enum là domain; contracts giữ bản đối chiếu cho DTO/response.
 */
export const PEER_CONFIRM_MODE = {
  MANUAL_ONLY: "manual_only",
  AUTO_AFTER_72H: "auto_after_72h",
  HOLD_UNTIL_CREDITOR: "hold_until_creditor",
  REQUIRE_HANDSHAKE: "require_handshake",
} as const;

export type PeerConfirmMode =
  (typeof PEER_CONFIRM_MODE)[keyof typeof PEER_CONFIRM_MODE];

/* ---------------------------------------------------------------------------
 * Config keys cấp nhóm (system_settings) — ngưỡng auto-confirm peer.
 * ------------------------------------------------------------------------- */

export const PEER_CONFIRM_AUTO_THRESHOLD_MINOR_KEY = "peer_confirm_auto_threshold_minor";
export const PEER_CONFIRM_AUTO_HOURS_KEY = "peer_confirm_auto_hours";
export const PEER_CONFIRM_MANUAL_THRESHOLD_MINOR_KEY = "peer_confirm_manual_threshold_minor";

/** Default nghiệp vụ (khớp seed migration + domain PEER_CONFIRM_DEFAULTS). */
export const PEER_CONFIRM_AUTO_THRESHOLD_MINOR_DEFAULT = "200000";
export const PEER_CONFIRM_AUTO_HOURS_DEFAULT = 72;
export const PEER_CONFIRM_MANUAL_THRESHOLD_MINOR_DEFAULT = "2000000";

/**
 * Config ngưỡng xác nhận peer (cấp nhóm). Tiền = string minor (B1). autoThreshold < manualThreshold
 * (khoảng giữa "treo tới creditor" không rỗng). autoAfterHours ≥ 1 (số giờ chờ auto). .strict().
 */
export const peerConfirmThresholdConfigSchema = z
  .object({
    autoThresholdMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    manualThresholdMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    autoAfterHours: z.number().int().positive(),
  })
  .strict()
  .refine(
    (v) => BigInt(v.autoThresholdMinor) < BigInt(v.manualThresholdMinor),
    { message: "autoThresholdMinor phải < manualThresholdMinor", path: ["autoThresholdMinor"] },
  );

export type PeerConfirmThresholdConfig = z.infer<
  typeof peerConfirmThresholdConfigSchema
>;
